<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>学校管理</h1>
        <p>学校名录、所在地区、认证邮箱域名与开通进度</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" class="primary-btn" icon="el-icon-plus" @click="handleCreate">
          新增学校
        </el-button>
      </div>
    </div>

    <!-- ── 开通规则说明：这个规则不看一眼很容易忘 ───────────── -->
    <div class="hint-bar">
      <i class="el-icon-info" />
      <span>
        新建的学校默认为「筹备中」，<b>用户端看不到</b>；设为「试点中」后才出现在用户端，
        并在校名后挂
        <b>「{{ pilotBadgeText }}」</b>
        标签；升到「正式运营」后标签自动消失。
      </span>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="listQuery.keyword"
        placeholder="搜索学校名称"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-select v-model="listQuery.launchStatus" placeholder="全部开通状态" clearable class="status-select" @change="handleFilter">
        <el-option label="筹备中" :value="0" />
        <el-option label="试点中" :value="1" />
        <el-option label="正式运营" :value="2" />
      </el-select>
      <el-select v-model="listQuery.province" placeholder="全部省份" clearable class="region-select" @change="handleProvinceChange">
        <el-option v-for="province in provinces" :key="province" :label="province" :value="province" />
      </el-select>
      <el-select v-model="listQuery.city" placeholder="全部城市" clearable class="region-select" :disabled="!listQuery.province" @change="handleFilter">
        <el-option v-for="city in cities" :key="city" :label="city" :value="city" />
      </el-select>
      <el-select v-model="listQuery.status" placeholder="全部启停状态" clearable class="status-select" @change="handleFilter">
        <el-option label="已启用" :value="1" />
        <el-option label="已停用" :value="0" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleFilter">查询</el-button>
      <el-button class="ghost-btn" @click="resetFilter">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 所</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column label="学校名称" min-width="190">
          <template slot-scope="{ row }">
            <div class="cell-main">{{ row.name }}</div>
          </template>
        </el-table-column>
        <el-table-column label="地区" width="150">
          <template slot-scope="{ row }">
            <span v-if="row.province || row.city" class="cell-sub">{{ row.province }} {{ row.city }}</span>
            <span v-else class="cell-sub">—</span>
          </template>
        </el-table-column>
        <el-table-column label="邮箱域名" min-width="160">
          <template slot-scope="{ row }">
            <span v-if="row.emailDomain" class="tag is-mono">{{ row.emailDomain }}</span>
            <span v-else class="cell-sub">—</span>
          </template>
        </el-table-column>
        <el-table-column label="用户数" width="90" align="right">
          <template slot-scope="{ row }">
            <span class="cell-main">{{ row.userCount || 0 }}</span>
          </template>
        </el-table-column>
        <!-- 开通状态 = 对外开放到哪一步；与右侧的启停状态是两个维度 -->
        <el-table-column label="开通状态" width="120">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="launchPillClass(row.launchStatus)">
              <i />{{ launchText(row.launchStatus) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="启停" width="100">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="row.status === 1 ? 's1' : ''">
              <i />{{ row.status === 1 ? '启用' : '停用' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="160">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatDate(row.createdAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <!-- 「下一步」动作：按当前档位给唯一推荐动作，运营不用思考该点哪个 -->
              <button
                v-if="nextLaunchAction(row).show"
                type="button"
                class="act"
                :class="nextLaunchAction(row).cls"
                @click="handleLaunchChange(row, nextLaunchAction(row).value)"
              >
                {{ nextLaunchAction(row).label }}
              </button>
              <button type="button" class="act act-plain" @click="handleUpdate(row)">编辑</button>
              <button
                type="button"
                class="act"
                :class="row.status === 1 ? 'act-danger' : 'act-primary'"
                @click="handleToggleStatus(row)"
              >
                {{ row.status === 1 ? '停用' : '恢复' }}
              </button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          v-show="total > 0"
          background
          :current-page="listQuery.page"
          :page-size="listQuery.size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- ── 新增 / 编辑 ────────────────────────────────────── -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px">
      <el-form ref="form" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="学校名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入学校全称" />
        </el-form-item>
        <el-form-item label="省份" prop="province">
          <el-select v-model="form.province" placeholder="请选择省份" style="width: 100%;" @change="handleFormProvinceChange">
            <el-option v-for="province in provinces" :key="province" :label="province" :value="province" />
          </el-select>
        </el-form-item>
        <el-form-item label="城市" prop="city">
          <el-select v-model="form.city" placeholder="请选择城市" style="width: 100%;" :disabled="!form.province">
            <el-option v-for="city in formCities" :key="city" :label="city" :value="city" />
          </el-select>
        </el-form-item>
        <el-form-item label="邮箱域名" prop="emailDomain">
          <el-input v-model="form.emailDomain" placeholder="@xxx.edu.cn" />
        </el-form-item>
        <el-form-item label="开通状态" prop="launchStatus">
          <!-- 三档卡片单选：一眼看清每档对用户端意味着什么，比下拉更不容易选错 -->
          <div class="launch-pick">
            <div
              v-for="opt in launchOptions"
              :key="opt.value"
              class="launch-pick__item"
              :class="{ 'launch-pick__item--on': form.launchStatus === opt.value }"
              @click="form.launchStatus = opt.value"
            >
              <div class="launch-pick__t">{{ opt.label }}</div>
              <div class="launch-pick__d">{{ opt.desc }}</div>
            </div>
          </div>
          <div class="launch-note">试点中会挂「{{ pilotBadgeText }}」标签，文案在「系统配置 → 学校开通」里改。</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getSchoolList, createSchool, updateSchool, deleteSchool, updateSchoolLaunchStatus } from '@/api/school'
// 标签文案走"公开配置"通道（与首页读 bean.per_yuan 同一个接口），
// 不碰 /admin/config/list —— 那个接口挂的是「系统配置」菜单权限，
// 学校管理岗不该为了读一个展示文案而被要求拿到系统配置的菜单。
import { getPublicConfigs } from '@/api/system'

/** 三档开通状态。值与后端 com.xiaobai.constant.SchoolLaunchStatus 一致 */
const LAUNCH_DRAFT = 0
const LAUNCH_PILOT = 1
const LAUNCH_LIVE = 2

export default {
  name: 'School',
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        size: 10,
        keyword: '',
        province: '',
        city: '',
        status: '',
        launchStatus: ''
      },
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      form: {
        id: null,
        name: '',
        province: '',
        city: '',
        emailDomain: '',
        launchStatus: LAUNCH_DRAFT
      },
      formCities: [],
      provinces: [],
      cities: [],
      /** 标签文案来自后端配置，不在前端写死 —— 与小程序端读的是同一个值 */
      pilotBadgeText: '新开通',
      rules: {
        name: [{ required: true, message: '请输入学校名称', trigger: 'blur' }],
        province: [{ required: true, message: '请选择省份', trigger: 'change' }],
        city: [{ required: true, message: '请选择城市', trigger: 'change' }],
        emailDomain: [{ required: true, message: '请输入邮箱域名', trigger: 'blur' }],
        launchStatus: [{ required: true, message: '请选择开通状态', trigger: 'change' }]
      }
    }
  },
  computed: {
    launchOptions() {
      return [
        { value: LAUNCH_DRAFT, label: '筹备中', desc: '用户端不展示' },
        { value: LAUNCH_PILOT, label: '试点中', desc: `展示并挂「${this.pilotBadgeText}」标` },
        { value: LAUNCH_LIVE, label: '正式运营', desc: '展示，不挂标' }
      ]
    }
  },
  created() {
    this.fetchData()
    this.loadProvinceCityData()
    this.fetchPilotBadgeText()
  },
  methods: {
    /**
     * 标签文案读后端配置，保证管理端提示与用户端实际展示完全一致。
     * 读不到不报错也不阻断页面 —— 兜底值就是配置的默认值（"新开通"），
     * 这里失败不该影响学校管理的任何操作。
     */
    fetchPilotBadgeText() {
      getPublicConfigs().then(res => {
        const list = (res && res.data) || []
        const item = list.find(r => r.configKey === 'school.pilot_badge_text')
        if (item && item.configValue) {
          this.pilotBadgeText = item.configValue
        }
      }).catch(() => {})
    },
    launchText(v) {
      if (v === LAUNCH_PILOT) return '试点中'
      if (v === LAUNCH_LIVE) return '正式运营'
      return '筹备中'
    },
    /** 与设计稿一致：筹备中=中性灰，试点中=玫瑰，正式运营=墨绿 */
    launchPillClass(v) {
      if (v === LAUNCH_PILOT) return 's2'
      if (v === LAUNCH_LIVE) return 's1'
      return ''
    },
    /** 当前档位的"下一步"动作。正式运营已是终态，不再给推荐动作 */
    nextLaunchAction(row) {
      const v = this.nvlLaunch(row.launchStatus)
      if (v === LAUNCH_DRAFT) {
        return { show: true, label: '开通试点', cls: 'act-primary', value: LAUNCH_PILOT }
      }
      if (v === LAUNCH_PILOT) {
        return { show: true, label: '转正式', cls: 'act-primary', value: LAUNCH_LIVE }
      }
      return { show: true, label: '退回试点', cls: 'act-plain', value: LAUNCH_PILOT }
    },
    nvlLaunch(v) {
      return v === null || v === undefined ? LAUNCH_DRAFT : v
    },
    loadProvinceCityData() {
      this.provinces = [
        '北京市', '上海市', '天津市', '重庆市',
        '河北省', '山西省', '辽宁省', '吉林省', '黑龙江省',
        '江苏省', '浙江省', '安徽省', '福建省', '江西省', '山东省',
        '河南省', '湖北省', '湖南省', '广东省', '海南省',
        '四川省', '贵州省', '云南省', '陕西省', '甘肃省', '青海省',
        '内蒙古自治区', '广西壮族自治区', '西藏自治区', '宁夏回族自治区', '新疆维吾尔自治区',
        '台湾省', '香港特别行政区', '澳门特别行政区'
      ]

      this.provinceCityMap = {
        '北京市': ['北京市'],
        '上海市': ['上海市'],
        '天津市': ['天津市'],
        '重庆市': ['重庆市'],
        '河北省': ['石家庄市', '唐山市', '秦皇岛市', '邯郸市', '邢台市', '保定市', '张家口市', '承德市', '沧州市', '廊坊市', '衡水市'],
        '山西省': ['太原市', '大同市', '阳泉市', '长治市', '晋城市', '朔州市', '晋中市', '运城市', '忻州市', '临汾市', '吕梁市'],
        '辽宁省': ['沈阳市', '大连市', '鞍山市', '抚顺市', '本溪市', '丹东市', '锦州市', '营口市', '阜新市', '辽阳市', '盘锦市', '铁岭市', '朝阳市', '葫芦岛市'],
        '吉林省': ['长春市', '吉林市', '四平市', '辽源市', '通化市', '白山市', '松原市', '白城市', '延边朝鲜族自治州'],
        '黑龙江省': ['哈尔滨市', '齐齐哈尔市', '鸡西市', '鹤岗市', '双鸭山市', '大庆市', '伊春市', '佳木斯市', '七台河市', '牡丹江市', '黑河市', '绥化市', '大兴安岭地区'],
        '江苏省': ['南京市', '无锡市', '徐州市', '常州市', '苏州市', '南通市', '连云港市', '淮安市', '盐城市', '扬州市', '镇江市', '泰州市', '宿迁市'],
        '浙江省': ['杭州市', '宁波市', '温州市', '嘉兴市', '湖州市', '绍兴市', '金华市', '衢州市', '舟山市', '台州市', '丽水市'],
        '安徽省': ['合肥市', '芜湖市', '蚌埠市', '淮南市', '马鞍山市', '淮北市', '铜陵市', '安庆市', '黄山市', '滁州市', '阜阳市', '宿州市', '六安市', '亳州市', '池州市', '宣城市'],
        '福建省': ['福州市', '厦门市', '莆田市', '三明市', '泉州市', '漳州市', '南平市', '龙岩市', '宁德市'],
        '江西省': ['南昌市', '景德镇市', '萍乡市', '九江市', '新余市', '鹰潭市', '赣州市', '吉安市', '宜春市', '抚州市', '上饶市'],
        '山东省': ['济南市', '青岛市', '淄博市', '枣庄市', '东营市', '烟台市', '潍坊市', '济宁市', '泰安市', '威海市', '日照市', '临沂市', '德州市', '聊城市', '滨州市', '菏泽市'],
        '河南省': ['郑州市', '开封市', '洛阳市', '平顶山市', '安阳市', '鹤壁市', '新乡市', '焦作市', '濮阳市', '许昌市', '漯河市', '三门峡市', '南阳市', '商丘市', '信阳市', '周口市', '驻马店市'],
        '湖北省': ['武汉市', '黄石市', '十堰市', '宜昌市', '襄阳市', '鄂州市', '荆门市', '孝感市', '荆州市', '黄冈市', '咸宁市', '随州市', '恩施土家族苗族自治州'],
        '湖南省': ['长沙市', '株洲市', '湘潭市', '衡阳市', '邵阳市', '岳阳市', '常德市', '张家界市', '益阳市', '郴州市', '永州市', '怀化市', '娄底市', '湘西土家族苗族自治州'],
        '广东省': ['广州市', '韶关市', '深圳市', '珠海市', '汕头市', '佛山市', '江门市', '湛江市', '茂名市', '肇庆市', '惠州市', '梅州市', '汕尾市', '河源市', '阳江市', '清远市', '东莞市', '中山市', '潮州市', '揭阳市', '云浮市'],
        '海南省': ['海口市', '三亚市', '三沙市', '儋州市', '省直辖县级行政区'],
        '四川省': ['成都市', '自贡市', '攀枝花市', '泸州市', '德阳市', '绵阳市', '广元市', '遂宁市', '内江市', '乐山市', '南充市', '眉山市', '宜宾市', '广安市', '达州市', '雅安市', '巴中市', '资阳市', '阿坝藏族羌族自治州', '甘孜藏族自治州', '凉山彝族自治州'],
        '贵州省': ['贵阳市', '六盘水市', '遵义市', '安顺市', '毕节市', '铜仁市', '黔西南布依族苗族自治州', '黔东南苗族侗族自治州', '黔南布依族苗族自治州'],
        '云南省': ['昆明市', '曲靖市', '玉溪市', '保山市', '昭通市', '丽江市', '普洱市', '临沧市', '楚雄彝族自治州', '红河哈尼族彝族自治州', '文山壮族苗族自治州', '西双版纳傣族自治州', '大理白族自治州', '德宏傣族景颇族自治州', '怒江傈僳族自治州', '迪庆藏族自治州'],
        '陕西省': ['西安市', '铜川市', '宝鸡市', '咸阳市', '渭南市', '延安市', '汉中市', '榆林市', '安康市', '商洛市'],
        '甘肃省': ['兰州市', '嘉峪关市', '金昌市', '白银市', '天水市', '武威市', '张掖市', '平凉市', '酒泉市', '庆阳市', '定西市', '陇南市', '临夏回族自治州', '甘南藏族自治州'],
        '青海省': ['西宁市', '海东市', '海北藏族自治州', '黄南藏族自治州', '海南藏族自治州', '果洛藏族自治州', '玉树藏族自治州', '海西蒙古族藏族自治州'],
        '内蒙古自治区': ['呼和浩特市', '包头市', '乌海市', '赤峰市', '通辽市', '鄂尔多斯市', '呼伦贝尔市', '巴彦淖尔市', '乌兰察布市', '兴安盟', '锡林郭勒盟', '阿拉善盟'],
        '广西壮族自治区': ['南宁市', '柳州市', '桂林市', '梧州市', '北海市', '防城港市', '钦州市', '贵港市', '玉林市', '百色市', '贺州市', '河池市', '来宾市', '崇左市'],
        '西藏自治区': ['拉萨市', '日喀则市', '昌都市', '林芝市', '山南市', '那曲市', '阿里地区'],
        '宁夏回族自治区': ['银川市', '石嘴山市', '吴忠市', '固原市', '中卫市'],
        '新疆维吾尔自治区': ['乌鲁木齐市', '克拉玛依市', '吐鲁番市', '哈密市', '昌吉回族自治州', '博尔塔拉蒙古自治州', '巴音郭楞蒙古自治州', '阿克苏地区', '克孜勒苏柯尔克孜自治州', '喀什地区', '和田地区', '伊犁哈萨克自治州', '塔城地区', '阿勒泰地区'],
        '台湾省': ['台北市', '高雄市', '台南市', '台中市', '新北市', '桃园市', '新竹市', '基隆市', '嘉义市', '省直辖县'],
        '香港特别行政区': ['香港岛', '九龙', '新界'],
        '澳门特别行政区': ['澳门半岛', '氹仔', '路环']
      }
    },
    fetchData() {
      this.listLoading = true
      getSchoolList(this.listQuery).then(res => {
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.listLoading = false
      })
    },
    handleFilter() {
      this.listQuery.page = 1
      this.fetchData()
    },
    resetFilter() {
      this.listQuery.keyword = ''
      this.listQuery.province = ''
      this.listQuery.city = ''
      this.listQuery.status = ''
      this.listQuery.launchStatus = ''
      this.cities = []
      this.handleFilter()
    },
    handleProvinceChange(province) {
      this.listQuery.city = ''
      if (province) {
        this.cities = this.provinceCityMap[province] || []
      } else {
        this.cities = []
      }
      this.handleFilter()
    },
    handleFormProvinceChange(province) {
      this.form.city = ''
      if (province) {
        this.formCities = this.provinceCityMap[province] || []
      } else {
        this.formCities = []
      }
    },
    handleSizeChange(val) {
      this.listQuery.size = val
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchData()
    },
    handleCreate() {
      this.resetForm()
      this.dialogTitle = '新增学校'
      this.isEdit = false
      this.dialogVisible = true
    },
    handleUpdate(row) {
      this.resetForm()
      this.form = { ...row, launchStatus: this.nvlLaunch(row.launchStatus) }
      if (this.form.province) {
        this.formCities = this.provinceCityMap[this.form.province] || []
      }
      this.dialogTitle = '编辑学校'
      this.isEdit = true
      this.dialogVisible = true
    },
    /**
     * 快捷切换开通状态。
     * 「开通试点」会让学校立刻出现在用户端，所以要多一次确认 —— 但确认文案只说
     * 会发生什么，不说"你确定吗"，运营自己知道自己在干什么。
     */
    handleLaunchChange(row, target) {
      const targetText = this.launchText(target)
      let content = `确定把「${row.name}」设为「${targetText}」吗？`
      if (target === LAUNCH_PILOT) {
        content = `设为「试点中」后，该学校会立刻出现在用户端可选列表里，并挂上「${this.pilotBadgeText}」标签。`
      } else if (target === LAUNCH_LIVE) {
        content = `设为「正式运营」后，该学校继续在用户端展示，但「${this.pilotBadgeText}」标签会消失。`
      } else if (target === LAUNCH_DRAFT) {
        content = `设为「筹备中」后，该学校会从用户端可选列表里消失（已在读的学生资料不受影响）。`
      }
      this.$confirm(content, `设为${targetText}`, {
        type: target === LAUNCH_DRAFT ? 'warning' : 'info',
        confirmButtonText: `设为${targetText}`,
        cancelButtonText: '取消'
      }).then(() => {
        updateSchoolLaunchStatus(row.id, target).then(res => {
          this.$message.success(res.message || '设置成功')
          this.fetchData()
        })
      }).catch(() => {})
    },
    handleToggleStatus(row) {
      const action = row.status === 1 ? '停用' : '恢复'
      const content = row.status === 1
        ? `停用后「${row.name}」在用户端不再展示，已在该校的 ${row.userCount || 0} 名学生的资料与纸条不受影响。`
        : `恢复后「${row.name}」将按当前开通状态（${this.launchText(row.launchStatus)}）重新在用户端展示。`
      this.$confirm(content, `${action}学校`, {
        type: row.status === 1 ? 'warning' : 'info',
        confirmButtonText: `确定${action}`,
        cancelButtonText: '取消',
        // 停用是"会让学校从用户端消失"的动作，确认键用危险色，不用品牌玫瑰
        confirmButtonClass: row.status === 1 ? 'el-button--danger' : ''
      }).then(() => {
        deleteSchool(row.id).then(() => {
          this.$message.success(`${action}成功`)
          this.fetchData()
        })
      }).catch(() => {})
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            updateSchool(this.form).then(() => {
              this.$message.success('修改成功')
              this.dialogVisible = false
              this.fetchData()
            })
          } else {
            createSchool(this.form).then(() => {
              this.$message.success('创建成功')
              this.dialogVisible = false
              this.fetchData()
            })
          }
        }
      })
    },
    resetForm() {
      this.form = {
        id: null,
        name: '',
        province: '',
        city: '',
        emailDomain: '',
        launchStatus: LAUNCH_DRAFT
      }
      this.formCities = []
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(String(date).replace(/-/g, '/')).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
.region-select {
  width: 150px;
}

.status-select {
  width: 140px;
}

/* ---------- 开通状态三选一卡片（仅本页结构） ---------- */
.launch-pick {
  display: flex;
  gap: 10px;
}

.launch-pick__item {
  flex: 1;
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  padding: 10px 12px;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s;
}

.launch-pick__item:hover {
  border-color: var(--ink-300);
}

.launch-pick__item--on {
  border-color: var(--rose-500);
  background: var(--rose-50);
  box-shadow: 0 0 0 2px rgba(201, 59, 87, 0.09);
}

.launch-pick__t {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-900);
  line-height: 1.3;
}

.launch-pick__item--on .launch-pick__t {
  color: var(--rose-600);
}

.launch-pick__d {
  font-size: 11.5px;
  color: var(--ink-400);
  margin-top: 4px;
  line-height: 1.5;
}

.launch-note {
  font-size: 12px;
  color: var(--ink-400);
  line-height: 1.6;
  margin-top: 7px;
}
</style>
